import React, { useEffect, useState, useCallback } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ChevronRight, Users, Package, ShoppingBag, CheckCircle, BarChart3, ShieldAlert, Tag, FolderTree, Download, Globe, FileText, Ticket, Send, Plug, Activity, Key, Percent, Bell } from 'lucide-react';
import { AdminAPI, API_BASE } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { useToast } from '../hooks/use-toast';
import { Button } from '../components/ui/button';
import ProductsTab from '../components/admin/ProductsTab';
import BrandsTab from '../components/admin/BrandsTab';
import CategoriesTab from '../components/admin/CategoriesTab';
import SiteSettingsTab from '../components/admin/SiteSettingsTab';
import BlogTab from '../components/admin/BlogTab';
import PromoCodesTab from '../components/admin/PromoCodesTab';
import SuppliersAdminTab from '../components/admin/SuppliersAdminTab';
import NotificationsTab from '../components/admin/NotificationsTab';
import OrdersAdminTab from '../components/admin/OrdersAdminTab';
import IntegrationsTab from '../components/admin/IntegrationsTab';
import SystemStatusTab from '../components/admin/SystemStatusTab';
import PartnerKeysTab from '../components/admin/PartnerKeysTab';
import PriceTiersTab from '../components/admin/PriceTiersTab';
import PushBroadcastTab from '../components/admin/PushBroadcastTab';
import MarketplaceTab from '../components/admin/MarketplaceTab';
import { useSiteConfig } from '../context/SiteConfigContext';

const AdminDashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const { refresh: refreshSiteConfig } = useSiteConfig();
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [pending, setPending] = useState([]);
  const [brands, setBrands] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loadingBrands, setLoadingBrands] = useState(true);
  const [loadingCats, setLoadingCats] = useState(true);

  const refreshTaxonomy = useCallback(async () => {
    setLoadingBrands(true); setLoadingCats(true);
    const [b, c] = await Promise.all([AdminAPI.brands(), AdminAPI.categories()]);
    setBrands(b); setCategories(c);
    setLoadingBrands(false); setLoadingCats(false);
  }, []);

  useEffect(() => {
    if (!user) { navigate('/connexion'); return; }
    if (user.role !== 'admin') { navigate('/'); return; }
    AdminAPI.stats().then(setStats);
    AdminAPI.users().then(setUsers);
    AdminAPI.pendingSuppliers().then(setPending);
    refreshTaxonomy();
  }, [user, navigate, refreshTaxonomy]);

  if (!user || user.role !== 'admin') return null;

  const approve = async (sid) => {
    await AdminAPI.approveSupplier(sid);
    toast({ title: 'Fournisseur validé' });
    AdminAPI.pendingSuppliers().then(setPending);
    AdminAPI.stats().then(setStats);
  };

  // Export helpers — déclenche un download via une URL authentifiée (token en query param)
  const downloadFile = async (format) => {
    try {
      const token = localStorage.getItem('dz_token');
      const res = await fetch(`${API_BASE}/admin/export?format=${format}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Export échoué');
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `comptoir_online_export_${new Date().toISOString().slice(0, 10)}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast({ title: `✅ Export ${format.toUpperCase()} téléchargé` });
    } catch (e) {
      toast({ title: 'Erreur', description: e.message });
    }
  };
  const exportJSON = () => downloadFile('json');
  const exportCSV = () => downloadFile('csv');

  const tabs = [
    { id: 'overview', label: 'Vue d\'ensemble', icon: BarChart3 },
    { id: 'system', label: 'Statut système', icon: Activity },
    { id: 'site', label: 'Site & contenu', icon: Globe },
    { id: 'blog', label: 'Blog', icon: FileText },
    { id: 'promos', label: 'Codes promo', icon: Ticket },
    { id: 'products', label: 'Produits', icon: Package },
    { id: 'brands', label: `Marques (${brands.length})`, icon: Tag },
    { id: 'categories', label: `Catégories (${categories.length})`, icon: FolderTree },
    { id: 'orders', label: 'Commandes', icon: ShoppingBag },
    { id: 'suppliers', label: `Fournisseurs${pending.length ? ` · ${pending.length} en attente` : ''}`, icon: ShieldAlert },
    { id: 'marketplace', label: 'Occasion & Réparateurs', icon: Package },
    { id: 'notifications', label: 'Notifications', icon: Send },
    { id: 'push', label: 'Push', icon: Bell },
    { id: 'integrations', label: 'Intégrations', icon: Plug },
    { id: 'partner-api', label: 'Partner API', icon: Key },
    { id: 'price-tiers', label: 'Tarifs Pro', icon: Percent },
    { id: 'users', label: 'Utilisateurs', icon: Users },
  ];

  return (
    <div className="max-w-7xl mx-auto px-4 py-8">
      <nav className="flex items-center gap-1.5 text-sm text-[#7A6F5C] mb-4">
        <Link to="/" className="hover:text-[#1F4D3F]">Accueil</Link>
        <ChevronRight className="w-3.5 h-3.5" />
        <span className="text-[#2D2A26]">Administration</span>
      </nav>
      <div className="flex items-center gap-3 mb-6">
        <div className="w-10 h-10 rounded-lg bg-[#1F4D3F] flex items-center justify-center"><ShieldAlert className="w-5 h-5 text-white" /></div>
        <div>
          <h1 className="font-serif text-3xl lg:text-4xl font-semibold text-[#1F4D3F]">Administration</h1>
          <p className="text-[#7A6F5C] text-sm">Gestion plateforme Comptoir-Online</p>
        </div>
      </div>

      <div className="flex gap-1 mb-6 border-b border-[#E8E0D0] overflow-x-auto no-scrollbar">
        {tabs.map((t) => {
          const Icon = t.icon;
          return (
            <button key={t.id} onClick={() => setTab(t.id)}
              data-testid={`admin-tab-${t.id}`}
              className={`px-4 py-2 -mb-px border-b-2 text-sm font-medium flex items-center gap-1.5 whitespace-nowrap transition-colors ${tab === t.id ? 'border-[#1F4D3F] text-[#1F4D3F]' : 'border-transparent text-[#7A6F5C] hover:text-[#1F4D3F]'}`}>
              <Icon className="w-4 h-4" /> {t.label}
            </button>
          );
        })}
      </div>

      {tab === 'overview' && stats && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { label: 'Utilisateurs', value: stats.users, icon: Users },
              { label: 'Acheteurs', value: stats.buyers, icon: Users },
              { label: 'Fournisseurs', value: stats.suppliers, icon: Package },
              { label: 'Produits', value: stats.products, icon: Package },
              { label: 'Commandes', value: stats.orders, icon: ShoppingBag },
              { label: 'En attente', value: stats.pending_suppliers, icon: ShieldAlert },
            ].map((s) => {
              const Icon = s.icon;
              return (
                <div key={s.label} className="bg-white border border-[#E8E0D0] rounded-xl p-4">
                  <Icon className="w-5 h-5 text-[#1F4D3F] mb-2" />
                  <div className="font-serif text-2xl font-semibold text-[#1F4D3F]">{s.value}</div>
                  <div className="text-xs text-[#7A6F5C]">{s.label}</div>
                </div>
              );
            })}
          </div>

          {/* Export catalogue */}
          <div className="mt-6 bg-white border border-[#E8E0D0] rounded-xl p-5">
            <h2 className="font-serif text-lg font-semibold text-[#1F4D3F] mb-1">Export du catalogue</h2>
            <p className="text-sm text-[#7A6F5C] mb-4">Téléchargez la totalité de vos données (produits, marques, catégories, fournisseurs) pour archivage ou migration.</p>
            <div className="flex flex-wrap gap-2">
              <Button onClick={exportJSON} variant="outline" className="border-[#1F4D3F] text-[#1F4D3F]" data-testid="admin-export-json">
                <Download className="w-4 h-4 mr-1.5" /> Export JSON complet
              </Button>
              <Button onClick={exportCSV} variant="outline" className="border-[#1F4D3F] text-[#1F4D3F]" data-testid="admin-export-csv">
                <Download className="w-4 h-4 mr-1.5" /> Export CSV (produits + variantes)
              </Button>
            </div>
          </div>
        </>
      )}

      {tab === 'marketplace' && (
        <MarketplaceTab />
      )}
      {tab === 'products' && (
        <ProductsTab brands={brands} categories={categories} onRefresh={() => { AdminAPI.stats().then(setStats); refreshTaxonomy(); }} />
      )}

      {tab === 'site' && (
        <SiteSettingsTab />
      )}

      {tab === 'blog' && (
        <BlogTab />
      )}

      {tab === 'promos' && (
        <PromoCodesTab />
      )}

      {tab === 'brands' && (
        <BrandsTab brands={brands} loading={loadingBrands} onRefresh={refreshTaxonomy} />
      )}

      {tab === 'categories' && (
        <CategoriesTab categories={categories} loading={loadingCats} onRefresh={refreshTaxonomy} />
      )}

      {tab === 'suppliers' && (
        <SuppliersAdminTab />
      )}

      {tab === 'orders' && (
        <OrdersAdminTab />
      )}

      {tab === 'notifications' && (
        <NotificationsTab />
      )}

      {tab === 'push' && (
        <PushBroadcastTab />
      )}

      {tab === 'integrations' && (
        <IntegrationsTab />
      )}

      {tab === 'partner-api' && (
        <PartnerKeysTab />
      )}

      {tab === 'price-tiers' && (
        <PriceTiersTab />
      )}

      {tab === 'system' && (
        <SystemStatusTab />
      )}

      {tab === 'pending' && (
        <div className="bg-white border border-[#E8E0D0] rounded-2xl overflow-hidden">
          {pending.length === 0 ? (
            <div className="p-10 text-center text-[#7A6F5C]"><CheckCircle className="w-10 h-10 mx-auto mb-3 text-[#2D6452]" />Aucun fournisseur en attente.</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-[#FAF7F2] text-left text-[#7A6F5C] text-xs uppercase tracking-wider">
                <tr><th className="px-4 py-3">Société</th><th className="px-4 py-3">Ville</th><th className="px-4 py-3"></th></tr>
              </thead>
              <tbody>
                {pending.map((s) => (
                  <tr key={s.id} className="border-t border-[#F5F1EA]">
                    <td className="px-4 py-3 font-medium text-[#2D2A26]">{s.name}</td>
                    <td className="px-4 py-3 text-[#5A5247]">{s.city}</td>
                    <td className="px-4 py-3 text-right">
                      <Button size="sm" onClick={() => approve(s.id)} className="bg-[#1F4D3F] hover:bg-[#173B30] text-white"><CheckCircle className="w-3.5 h-3.5 mr-1" /> Valider</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {tab === 'users' && (
        <div className="bg-white border border-[#E8E0D0] rounded-2xl overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-[#FAF7F2] text-left text-[#7A6F5C] text-xs uppercase tracking-wider">
              <tr><th className="px-4 py-3">Nom</th><th className="px-4 py-3">Email</th><th className="px-4 py-3">Téléphone</th><th className="px-4 py-3">Rôle</th><th className="px-4 py-3">Validation</th></tr>
            </thead>
            <tbody>
              {users.map((u) => (
                <tr key={u.user_id} className="border-t border-[#F5F1EA]">
                  <td className="px-4 py-3 text-[#2D2A26]">{u.name}</td>
                  <td className="px-4 py-3 text-[#5A5247]">{u.email}</td>
                  <td className="px-4 py-3">{u.phone ? <a href={"tel:" + u.phone} className="text-[#C75B27] font-medium">{u.phone}</a> : <span className="text-[#9A8F82]">—</span>}</td>
                  <td className="px-4 py-3"><span className="px-2 py-1 rounded text-xs bg-[#F5F1EA] text-[#1F4D3F] capitalize">{u.role}</span></td>
                  <td className="px-4 py-3">{u.approved ? <CheckCircle className="w-4 h-4 text-[#2D6452]" /> : <span className="text-xs text-[#E0A52E]">En attente</span>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;

